import React from "react";
import { Card, CardContent, Grid, MenuItem, Typography } from "@material-ui/core";
import DotsMenu from "@icarius-common/dotsMenu";
import { useSelector } from "react-redux";
import { getAppColor } from "@icarius-app/selectors";
import { getProgressColor } from "@icarius-utils/colors";
import { createDateFromDDMMYYYY } from "@icarius-utils/date";
import { differenceInDays } from "date-fns";
import { formatNumberExactDecimals } from "@icarius-utils/format";
import { getLocalizedString } from "@icarius-localization/strings";

const splitIntoLines = (text) => {
    return text.split("\n").map((item, key) => {
        return (
            <span key={key}>
                {item}
                <br />
            </span>
        );
    });
};

const getStatusColor = (status) => {
    if (status === "Activo") return "var(--mainText)";
    if (status === "Detenido" || status === "Postergado") return "var(--progressLow)";
    return "var(--progressHigh)";
};

const getDelayOrRemaining = (endDate) => {
    const days = differenceInDays(
        new Date(createDateFromDDMMYYYY(endDate).setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0))
    );

    if (!days) return <span key={4}>{` ${getLocalizedString("expiresToday")}.`}</span>;

    if (days > 0) {
        return (
            <span key={4} style={{ color: "var(--progressHigh)" }}>{` ${getLocalizedString("daysRemaning", {
                remainingDays: Math.abs(days),
            })}`}</span>
        );
    }

    return (
        <span key={4} style={{ color: "var(--progressLow)" }}>{` ${getLocalizedString("daysDelay", {
            remainingDays: Math.abs(days),
        })}`}</span>
    );
};

const DataDisplay = ({ title, value, horizontal }) => {
    return (
        <Grid container item direction={horizontal ? "row" : "column"} xs={12} spacing={horizontal ? 1 : 0}>
            <Grid item>
                <Typography className="whiteText">{title}:</Typography>
            </Grid>
            <Grid item>
                <Typography style={{ fontWeight: 600, color: "var(--mainText)" }}>{value}</Typography>
            </Grid>
        </Grid>
    );
};

const Objective = ({ handlers, data, canOperate }) => {
    const color = useSelector(getAppColor);

    const menu = canOperate && (
        <DotsMenu>
            <MenuItem onClick={() => handlers.handleOpenEditDialogByUser(data.internalCode)}>
                {getLocalizedString("edit")}
            </MenuItem>
            <MenuItem onClick={() => handlers.handleOpenDeleteDialogByUser(data)}>
                {getLocalizedString("delete")}
            </MenuItem>
        </DotsMenu>
    );

    return (
        <Card style={{ width: "100%" }}>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid
                        container
                        item
                        xs={12}
                        style={{ marginBottom: 20, paddingBottom: 10, borderBottom: `1px solid ${color}` }}
                    >
                        <Grid container item xs={11} direction="column">
                            <Typography className="whiteText" style={{ fontSize: 20 }}>
                                {data.targetName}
                            </Typography>
                            <Typography style={{ fontWeight: 600, color: getStatusColor(data.statusName) }}>
                                {data.statusName}
                            </Typography>
                        </Grid>
                        <Grid container item xs={1}>
                            {menu}
                        </Grid>
                    </Grid>
                    <DataDisplay title={getLocalizedString("assignedBy")} value={data.assignerName} />
                    <div style={{ padding: 10 }} />
                    <DataDisplay title={getLocalizedString("assignationDate")} value={data.startDate} horizontal />
                    <DataDisplay title={getLocalizedString("endDate")} value={data.endDate} horizontal />
                    <DataDisplay title={getLocalizedString("progressStatus")} value={data.progressStatus} horizontal />
                    <DataDisplay
                        title={getLocalizedString("progressPercentageObjective")}
                        value={[
                            <span
                                key={1}
                                style={{ color: getProgressColor(data.progress * 100) }}
                            >{`${formatNumberExactDecimals(data.progress * 100, 2)}%`}</span>,
                            <span key={2}>{` ${getLocalizedString("atTime")} `}</span>,
                            <span key={3}>{`${data.progressDate}.`}</span>,
                            ["Por hacer", "En curso"].includes(data.progressStatus)
                                ? getDelayOrRemaining(data.endDate)
                                : null,
                        ]}
                        horizontal
                    />
                    {data.pendingApproval === "Si" && (
                        <Grid container item xs={12}>
                            <Typography style={{ fontWeight: 600, color: "var(--progressLow)" }}>
                                {getLocalizedString("youMustApproveObjective")}
                            </Typography>
                        </Grid>
                    )}
                    <div style={{ padding: 10 }} />
                    <DataDisplay
                        title={getLocalizedString("objectiveDescription")}
                        value={splitIntoLines(data.targetDescription || "-")}
                    />
                    <div style={{ padding: 10 }} />
                    <DataDisplay title={getLocalizedString("feedbacks")} value={data.feedbacksQuantity} horizontal />
                    <DataDisplay
                        title={getLocalizedString("relatedTask")}
                        value={data.relatedTask || getLocalizedString("noneFemale")}
                        horizontal
                    />
                    <div style={{ padding: 10 }} />
                    <DataDisplay
                        title={getLocalizedString("objectiveWeight")}
                        value={`${data.targetWeighting}%`}
                        horizontal
                    />
                </Grid>
            </CardContent>
        </Card>
    );
};

export default Objective;
